/**
 * This file should contain those methods which are needed in more than one order service file
 */

import { getFormatForDateFormat, getParsedNumber, roundNumber } from "../utils/Common";
import { getPaymentStatusForOrderList } from "./order-details-service";

export const showTableNumbers = (tableNumber, tables = []) => {
  if (!Array.isArray(tables) || tables.length === 0) {
    return tableNumber;
  }

  // Extract tableName and join
  return tables
    .map((t) => t.tableName)
    .filter(Boolean) // remove undefined/null values
    .join(", ");
};

export const redirectToTargetPage = (navigate) => {
  const queryParams = new URLSearchParams(window.location.search);
  const sourcePage = queryParams.get("sourcePage");
  const pageNO = queryParams.get("pageNO") || 1;

  if (sourcePage === "TABLE") {
    navigate("/table");
    return;
  }

  const destination = sourcePage === "Home" ? "/" : "/order";
  navigate(`${destination}?pageNo=${pageNO}`);
};

export const navToOrderEdit = (navigate, orderId, sourcePage, takeout = "No", pageNo = 1) => {
  const authStorage = JSON.parse(localStorage.getItem("auth-storage"));
  const tableManagement = authStorage.state?.tableManagement || "Inactive";
  navigate(
    `/order/${orderId}/edit?tablemanagement=${tableManagement}&takeout=${takeout}&sourcePage=${sourcePage}&pageNo=${pageNo}`
  );
};

export const navToPayment = (navigate, orderId, sourcePage, takeout = "No", pageNo = 1) => {
  const authStorage = JSON.parse(localStorage.getItem("auth-storage"));
  const tableManagement = authStorage.state?.tableManagement || "Inactive";
  navigate(
    `/order/${orderId}/payment?tablemanagement=${tableManagement}&takeout=${takeout}&sourcePage=${sourcePage}&pageNo=${pageNo}`
  );
};

export const navToSplitBill = (navigate, orderId, sourcePage, takeout = "No", pageNo = 1) => {
  const authStorage = JSON.parse(localStorage.getItem("auth-storage"));
  const tableManagement = authStorage.state?.tableManagement || "Inactive";
  navigate(
    `/order/${orderId}/split-bill?tablemanagement=${tableManagement}&takeout=${takeout}&sourcePage=${sourcePage}&pageNo=${pageNo}`
  );
};

export const navToOrderDetails = (navigate, orderId, sourcePage, pageNo = 1) => {
  navigate(`/order/${orderId}/details?sourcePage=${sourcePage}&pageNo=${pageNo}`);
};

/**
 * Extract phone and country code from full phone number i.e., 88017xxxxxxxx
 *
 * @param {*} fullPhoneNumber 88017xxxxxxxx
 * @returns
 */
export const getPhoneObj = (fullPhoneNumber) => {
  const lastTenDigits = fullPhoneNumber.slice(-10);

  return {
    countryCode: "880",
    phone: lastTenDigits,
  };
};

// Recalculate total price for the group
export const getTotalPriceOfSelectedItems = (group, additionItems, foodQuantity) => {
  // Total quantity of items in the group
  const totalSelectedQty = additionItems.reduce((acc, item) => acc + item.quantity, 0);

  // Chargeable quantity beyond free limit
  const totalNumberOfFreeItem = group.numberOfFreeItems * foodQuantity;
  const chargeableQty = Math.max(0, totalSelectedQty - totalNumberOfFreeItem);

  return roundNumber(chargeableQty * parseFloat(group.pricePerAddition));
};

export const getGroupsWithoutAllGroups = (orderFoodItemAdditionGroups) => {
  return orderFoodItemAdditionGroups.map((gItem) => ({
    ...gItem,
    pricePerAddition: getParsedNumber(gItem.pricePerAddition),
    totalPriceOfSelectedItems: getParsedNumber(gItem.totalPriceOfSelectedItems),
  }));
};

export const getGroupsWithAllGroups = (orderedFoodQty, orderedGroups, orgAllGroups) => {
  const idWiseItems = {};

  // Just to quick access of addition groups and their items
  orderedGroups.forEach((oiag) => {
    idWiseItems[oiag.additionGroupId] = oiag;
    oiag.additionItems.forEach((oiagItem) => {
      idWiseItems[`${oiag.additionGroupId}_${oiagItem.additionItemId}`] = oiagItem;
    });
  });

  return orgAllGroups.map((gItem) => {
    const groupItem = { ...gItem };

    groupItem["isSelected"] = idWiseItems.hasOwnProperty(groupItem.additionGroupId);
    groupItem.additionItems = groupItem.additionItems.map((additionItem) => {
      const key = `${groupItem.additionGroupId}_${additionItem.additionItemId}`;
      return {
        ...additionItem,
        quantity: idWiseItems.hasOwnProperty(key) ? idWiseItems[key].quantity : 0,
      };
    });

    groupItem["totalPriceOfSelectedItems"] = getTotalPriceOfSelectedItems(
      groupItem,
      groupItem.additionItems,
      orderedFoodQty
    );

    return groupItem;
  });
};

/**
 * This method will return meal addition groups with same format but on the basis of use case it may have all addition groups or only ordered addition groups
 * In order form page it will have all addition groups and in payment page it will not have all addition groups
 *
 * Note: After order submit it will be reformated to have only the ordered addition groups. And thus payment page will have always same data.
 *
 * @param {*} orderedItem
 * @param {*} orgItem
 */
export const getItemWiseFormatedMealGroups = (orderedFoodItem, orgFoodItem) => {
  if (orgFoodItem && Object.keys(orgFoodItem).length > 0) {
    return getGroupsWithAllGroups(orderedFoodItem.quantity, orderedFoodItem.additionGroups, orgFoodItem.additionGroups);
  }

  return getGroupsWithoutAllGroups(orderedFoodItem.additionGroups);
};

/**
 * This method can be called in order form or in payment form. In payment form idWiseFoods will not be available so ordered food price is the price
 * but in order form food's current price is the price
 *
 * @param {*} orderedFoods
 * @param {*} idWiseFoods
 * @returns
 */
export function getFormatedFoodItems(orderedFoods, idWiseFoods = {}) {
  const foodItems = orderedFoods.map((food) => {
    const orgFood = idWiseFoods[food.id];
    let targetOption = (orgFood?.options || []).find((op) => op.optionId === food.option.optionId);
    targetOption = targetOption === undefined ? food.option : targetOption;
    const hasAddOnsOrMealAdditions = orgFood.addOns.length > 0 || orgFood.additionGroups.length > 0 ? true : false;

    return {
      // fuid: crypto.randomUUID(),
      fuid: food.uid,
      foodId: food.id,
      foodType: orgFood?.foodType || food.foodType,
      name: orgFood?.name || food.name,
      optionName: targetOption.name,
      optionId: targetOption.optionId,
      quantity: food.quantity,
      weight: targetOption.weight,
      unitPrice: targetOption?.currentPrice || targetOption.price, // orgFood option will have current price (price after discount)
      addOns: food.addOns.map((addOn) => ({
        id: addOn.id,
        name: addOn.name,
        price: addOn.price,
      })),
      additionGroups: getItemWiseFormatedMealGroups(food, orgFood),
      hasAddOnsOrMealAdditions,
      isVatApplicable: food.isVatApplicable,
      isSupplementaryDutyApplicable: food.isSupplementaryDutyApplicable,
      supplementaryDuty: food.supplementaryDuty,
    };
  });

  return foodItems;
}

export const formatAsOrderListFromFormData = (apiResOrder, formInputs) => {
  const discountApplicablePrice = roundNumber(
    apiResOrder.subtotalObj.vatApplicableDiscountApplicableSubtotal +
      apiResOrder.subtotalObj.vatNotApplicableDiscountApplicableSubtotal
  );

  return {
    _id: apiResOrder.id,
    orderId: apiResOrder.id,
    order_id: apiResOrder.orderNumber,
    orderNumber: apiResOrder.orderNumber,
    tableNumber: formInputs.tableNumber,
    guests: formInputs.guestNo || "",
    date: getFormatForDateFormat(new Date()),
    name: formInputs.phone,
    countryCode: formInputs.countryCode,
    number: `${formInputs.countryCode}${formInputs.phone}`,
    userId: apiResOrder.userId,
    customerName: apiResOrder.userName || "",
    isVatIncluded: formInputs.isVatIncluded,
    isServiceChargeIncluded: apiResOrder.isServiceChargeIncluded,
    isSupplementaryDutyIncluded: apiResOrder.isSupplementaryDutyIncluded,
    serviceChargePercent: formInputs.serviceChargePercent,
    vatPercent: formInputs.vatPercent,
    sd: apiResOrder.sd,
    serviceCharge: apiResOrder.serviceCharge,
    vat: apiResOrder.vat,
    total: formInputs.totalPrice,
    totalPriceBeforeDiscount: formInputs.totalPrice,
    discountApplicablePrice: discountApplicablePrice,
    foodPrice: apiResOrder.foodPrice, // new
    foodPriceBeforeDiscount: apiResOrder.foodPrice,
    subtotalObj: apiResOrder.subtotalObj,
    sdObj: apiResOrder.sdObj,

    paymentStatusText: getPaymentStatusForOrderList({ paymentStatus: 0, payAfterEat: formInputs.payAfterEat }), // as only unpaid order submitted with relavent mehtod
    payAfterEat: formInputs.payAfterEat.toLowerCase() === "yes",
    refundAmount: 0,
    refundInfo: null,
    totalFoodItems: formInputs.foods.length,
    delivery_time: "",
    comment: formInputs.comment,
    orderType: formInputs.orderType,
    status: apiResOrder.orderStatus.toLowerCase(),
    paid: 0, // only unpaid order submitted with relavent method
    staffDiscount: null,
    loyaltyDiscount: null,
    couponDiscount: null,
    paginationPageNo: 1,
  };
};

export const formatAsOrderListFromOrderDetails = (order) => {
  const userPhoneObj = getPhoneObj(order.userPhone);
  let discountApplicablePrice = 0;

  if (order.subtotalObj) {
    discountApplicablePrice = roundNumber(
      order.subtotalObj.vatApplicableDiscountApplicableSubtotal +
        order.subtotalObj.vatNotApplicableDiscountApplicableSubtotal
    );
  }

  return {
    _id: order.id,
    orderId: order.id,
    order_id: order.orderNumber,
    orderNumber: order.orderNumber,
    tableNumber: order.tableNumber,
    tables: order.tables || [],
    guests: order.guests || "",
    date: getFormatForDateFormat(order.createdAt),
    name: userPhoneObj.phone,
    countryCode: userPhoneObj.countryCode,
    number: order.userPhone,
    userId: order.userId,
    customerName: order.userName || "",
    total: order.totalPrice,
    totalPriceBeforeDiscount: order.totalPriceBeforeDiscount,
    foodPrice: order.foodPrice,
    foodPriceBeforeDiscount: order.foodPriceBeforeDiscount,
    serviceCharge: order.serviceCharge,
    sd: order.sd,
    serviceChargePercent: order.serviceChargePercent,
    vat: order.vat,
    vatPercent: order.vatPercent,
    isVatIncluded: order.isVatIncluded,
    isServiceChargeIncluded: order.isServiceChargeIncluded,
    isSupplementaryDutyIncluded: order.isSupplementaryDutyIncluded,
    totalPriceBeforeRounding: order.totalPriceBeforeRounding,
    discountApplicablePrice: discountApplicablePrice,
    subtotalObj: order.subtotalObj,
    sdObj: order.sdObj,

    paymentStatusText: getPaymentStatusForOrderList({
      paymentStatus: order.paymentStatus,
      payAfterEat: order.payAfterEat,
    }), // as only unpaid order submitted with relavent mehtod
    payAfterEat: order.payAfterEat,
    paymentType: order.paymentType,
    refundAmount: order?.refundInfo?.totalRefundAmount || "",
    refundInfo: order?.refundInfo ?? null,
    totalFoodItems: order.foods.length,
    delivery_time: getFormatForDateFormat(order.deliveryTime),
    comment: order.comment || "",
    orderType: order.orderType,
    status: order.orderStatus.toLowerCase(),
    paid: order.paymentStatus, // only unpaid order submitted with relavent method
    staffDiscount: order?.staffDiscount || null,
    loyaltyDiscount: order?.loyaltyDiscount || null,
    couponDiscount: order?.couponDiscount || null,
    paginationPageNo: 1,
  };
};

export const getPaymentCommonInfo = (
  paymentMethods,
  isSplitted,
  defaultRef,
  posBankId,
  totalPayable,
  discountType,
  discount
) => {
  const paymentReceiveTypes = paymentMethods
    .filter((item) => item.amount !== "") // it is commented to allow 0
    .map((item) => {
      item.paymentReceiveType = item.value;
      item.refNo = item.ref;

      if (!isSplitted) {
        item.ref = defaultRef;
        item.refNo = defaultRef;
      }

      if (!isSplitted && item.value.toLowerCase() === "pos") {
        item.posBankId = posBankId;
      }

      return item.value.toLowerCase() === "pos" ? Object.assign(item, { paymentReceiveType: "CARD" }) : item;
    });

  const totalPaidAmount = paymentMethods.reduce((sum, item) => sum + getParsedNumber(item.amount), 0);

  const roundedValues = JSON.parse(localStorage.getItem("paymentReceiveRoundedValues"));

  let totalReturnAmount = 0;
  if (roundedValues.isRoundingApplicable) {
    totalReturnAmount = totalPaidAmount - roundedValues.roundedPayable;
  } else {
    totalReturnAmount = totalPaidAmount - roundNumber(totalPayable);
  }

  let loyaltyDiscount = {
    discountType: discountType === "Point Based" ? "REDEEM" : "INSTANT_DISCOUNT",
    pointOrPercent: discount,
  };
  let actualDiscount = discount;

  if (["Point Based", "Spend Based"].includes(discountType)) {
    actualDiscount = 0;
  } else {
    loyaltyDiscount = undefined;
  }

  return {
    paymentReceiveTypes,
    totalPaidAmount,
    totalReturnAmount,
    amount: roundedValues.isRoundingApplicable ? roundedValues.roundedPayable : totalPayable,
    actualDiscount,
    isDiscountInPercent: discountType.toLowerCase() === "amount" ? "NO" : "YES",
    loyaltyDiscount
  };
};
